/**
 * Opportunity Detail Modal
 * Full-screen modal with tabbed interface for opportunity details
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Card, Button, Badge, Tabs, formatCurrency } from '@tracekit/core';
import { useQuery } from '@tanstack/react-query';
import { getAPIClient } from '@tracekit/core';
import {
  X,
  ExternalLink,
  FileText,
  Calendar,
  DollarSign,
  MapPin,
  Building2,
  Tag,
  Star,
  Clock,
  CheckCircle,
  AlertTriangle,
  Download,
  Share2,
  Bookmark,
  Search,
  ChevronDown,
  ChevronRight,
  Brain,
  Zap,
  StopCircle,
} from 'lucide-react';
import { GovtOpportunityEntity } from '../pages/OpportunitiesDashboard';
import {
  enhanceOpportunityEntity,
  getDeadlineUrgency,
  getOpportunityValue,
  getProjectIdDisplay,
  getStageBadgeColor,
} from '../utils/opportunityUtils';
import { ProposalSummaryCard } from './ProposalSummaryCard';
import { RequirementsTab } from './RequirementsTab';
import { OpportunityDocumentsTab } from './government-data/OpportunityDocumentsTab';
import { OpportunityEmailDraftTab } from './government-data/OpportunityEmailDraftTab';
import { useOpportunityAnalysis } from '../hooks/useOpportunityAnalysis';
import { useWorkspace } from '@tracekit/core';

interface OpportunityDetailModalProps {
  opportunity: GovtOpportunityEntity | null;
  isOpen: boolean;
  onClose: () => void;
  onSaveToList?: (listId: string) => void;
  onGenerateProposal?: () => void;
  onUpdateTracking?: (entityId: string, trackingStatus: string) => void; // For tracking status updates
  onOpportunityUpdate?: (updatedOpportunity: GovtOpportunityEntity) => void; // Callback to update opportunity in parent
}

export function OpportunityDetailModal({
  opportunity,
  isOpen,
  onClose,
  onSaveToList,
  onGenerateProposal,
  onUpdateTracking,
  onOpportunityUpdate,
}: OpportunityDetailModalProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'requirements' | 'analysis' | 'documents'>('overview');
  const [expandedCitation, setExpandedCitation] = useState<number | null>(null);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [analysisStage, setAnalysisStage] = useState<'idle' | 'retrieval' | 'thinking' | 'generating' | 'complete'>('idle');
  const [analysisElapsed, setAnalysisElapsed] = useState(0);
  const api = getAPIClient();
  
  // Loading messages that rotate during analysis
  const loadingMessages = [
    '🔍 Searching through knowledge base...',
    '📚 Analyzing documents...',
    '🧠 Processing with AI...',
    '💡 Generating insights...',
    '✨ Almost there...',
    '🎯 Finding the best evidence...',
  ];

  // Debug logging
  useEffect(() => {
    console.log('[OpportunityDetailModal] Props changed:', {
      isOpen,
      hasOpportunity: !!opportunity,
      opportunityId: opportunity?.id,
      opportunityTitle: opportunity?.data?.title,
    });
  }, [isOpen, opportunity]);

  const { projectId } = useWorkspace();
  
  // Fetch comprehensive analysis when Analysis tab is opened
  const { 
    data: analysis, 
    isLoading: analysisLoading,
    isError: analysisError,
    error: analysisErrorDetail,
  } = useOpportunityAnalysis(
    opportunity?.id || null,
    projectId || undefined,
    {
      include_technical_approaches: true,
      include_past_projects: true,
      include_staff_allocation: true,
      include_resource_estimation: true,
    },
    isOpen && activeTab === 'analysis' && !!opportunity?.id
  );

  // Track analysis loading stages and elapsed time
  useEffect(() => {
    if (analysisLoading) {
      setAnalysisStage('retrieval');
      const startTime = Date.now();
      const interval = setInterval(() => {
        setAnalysisElapsed((Date.now() - startTime) / 1000);
      }, 100);
      
      // Simulate stage progression
      const stageTimeout = setTimeout(() => setAnalysisStage('thinking'), 2000);
      const generatingTimeout = setTimeout(() => setAnalysisStage('generating'), 4000);
      
      return () => {
        clearInterval(interval);
        clearTimeout(stageTimeout);
        clearTimeout(generatingTimeout);
      };
    } else if (analysis) {
      setAnalysisStage('complete');
    } else {
      setAnalysisStage('idle');
      setAnalysisElapsed(0);
    }
  }, [analysisLoading, analysis]);

  // Rotate loading messages while analyzing
  useEffect(() => {
    if (!analysisLoading) return;
    
    const interval = setInterval(() => {
      setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 2000); // Change message every 2 seconds
    
    return () => clearInterval(interval);
  }, [analysisLoading, loadingMessages.length]);

  // Convert markdown to HTML for proper formatting with nested list support
  const convertMarkdownToHTML = (markdown: string): string => {
    if (!markdown) return '';
    
    // Remove extra metadata text at the top (like "{model_version} documents")
    let cleaned = markdown.replace(/^\{[^}]+\}\s*documents?\s*/i, '').trim();
    
    // Process line by line to handle lists and nested lists properly
    const lines = cleaned.split('\n');
    const processedBlocks: string[] = [];
    let currentList: { type: 'ul' | 'ol'; items: string[]; indent: number } | null = null;
    let currentParagraph: string[] = [];
    
    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        const paraText = currentParagraph.join(' ').trim();
        if (paraText) {
          processedBlocks.push(`<p class="mb-3 text-gray-700 dark:text-theme-text-secondary">${paraText}</p>`);
        }
        currentParagraph = [];
      }
    };
    
    const flushList = () => {
      if (currentList && currentList.items.length > 0) {
        const listTag = currentList.type;
        const indentClass = currentList.indent > 0 ? `ml-${currentList.indent * 4}` : 'ml-4';
        processedBlocks.push(`<${listTag} class="list-disc list-inside space-y-1 my-2 ${indentClass}">${currentList.items.join('')}</${listTag}>`);
        currentList = null;
      }
    };
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();
      
      if (!trimmed) {
        flushParagraph();
        flushList();
        continue;
      }
      
      // Check for numbered list (1. item)
      const numberedMatch = trimmed.match(/^(\d+)\.\s+(.+)$/);
      // Check for bullet list (- item or * item)
      const bulletMatch = trimmed.match(/^[-*•]\s+(.+)$/);
      // Check for nested bullet (indented with spaces or tabs)
      const indentMatch = line.match(/^(\s+)([-*•]|\d+\.)\s+(.+)$/);
      
      if (indentMatch) {
        // Nested list item
        const indentLevel = Math.floor(indentMatch[1].replace(/\t/g, '    ').length / 4);
        const isNumbered = /^\d+\./.test(indentMatch[2]);
        const content = indentMatch[3];
        
        flushParagraph();
        
        if (!currentList || currentList.indent !== indentLevel || 
            (isNumbered && currentList.type !== 'ol') || 
            (!isNumbered && currentList.type !== 'ul')) {
          flushList();
          currentList = {
            type: isNumbered ? 'ol' : 'ul',
            items: [],
            indent: indentLevel
          };
        }
        
        let itemContent = content;
        // Process inline formatting
        itemContent = itemContent.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-gray-900 dark:text-gray-100">$1</strong>');
        itemContent = itemContent.replace(/\[Source\s+(\d+)\]/gi, '<span class="text-xs text-blue-600 dark:text-blue-400 font-medium">[Source $1]</span>');
        currentList.items.push(`<li class="mb-1">${itemContent}</li>`);
      } else if (numberedMatch) {
        flushParagraph();
        if (!currentList || currentList.type !== 'ol' || currentList.indent !== 0) {
          flushList();
          currentList = { type: 'ol', items: [], indent: 0 };
        }
        let itemContent = numberedMatch[2];
        itemContent = itemContent.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-gray-900 dark:text-gray-100">$1</strong>');
        itemContent = itemContent.replace(/\[Source\s+(\d+)\]/gi, '<span class="text-xs text-blue-600 dark:text-blue-400 font-medium">[Source $1]</span>');
        currentList.items.push(`<li class="mb-1">${itemContent}</li>`);
      } else if (bulletMatch) {
        flushParagraph();
        if (!currentList || currentList.type !== 'ul' || currentList.indent !== 0) {
          flushList();
          currentList = { type: 'ul', items: [], indent: 0 };
        }
        let itemContent = bulletMatch[1];
        itemContent = itemContent.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-gray-900 dark:text-gray-100">$1</strong>');
        itemContent = itemContent.replace(/\[Source\s+(\d+)\]/gi, '<span class="text-xs text-blue-600 dark:text-blue-400 font-medium">[Source $1]</span>');
        currentList.items.push(`<li class="mb-1">${itemContent}</li>`);
      } else {
        // Regular paragraph text
        flushList();
        currentParagraph.push(trimmed);
      }
    }
    
    flushParagraph();
    flushList();
    
    let html = processedBlocks.join('');
    
    // Process inline formatting for paragraphs (bold, italic, links, citations)
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-gray-900 dark:text-gray-100">$1</strong>');
    html = html.replace(/__([^_]+)__/g, '<strong class="font-semibold text-gray-900 dark:text-gray-100">$1</strong>');
    html = html.replace(/\*([^*]+)\*/g, '<em class="italic text-gray-700 dark:text-gray-300">$1</em>');
    html = html.replace(/_([^_]+)_/g, '<em class="italic text-gray-700 dark:text-gray-300">$1</em>');
    html = html.replace(/\[Source\s+(\d+)\]/gi, '<span class="text-xs text-blue-600 dark:text-blue-400 font-medium">[Source $1]</span>');
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline" target="_blank" rel="noopener noreferrer">$1</a>');
    
    // Headers
    html = html.replace(/<p[^>]*>### (.+?)<\/p>/g, '<h3 class="text-base font-semibold text-gray-900 dark:text-theme-text mt-4 mb-2">$1</h3>');
    html = html.replace(/<p[^>]*>## (.+?)<\/p>/g, '<h2 class="text-lg font-bold text-gray-900 dark:text-theme-text mt-5 mb-3">$1</h2>');
    html = html.replace(/<p[^>]*># (.+?)<\/p>/g, '<h1 class="text-xl font-bold text-gray-900 dark:text-theme-text mt-6 mb-4">$1</h1>');
    
    return html || '<p class="text-gray-700 dark:text-theme-text-secondary">No content available.</p>';
  };

  useEffect(() => {
    // Lock body scroll when modal is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    // Close on Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen || !opportunity) {
    console.log('[OpportunityDetailModal] Not rendering:', { isOpen, hasOpportunity: !!opportunity });
    return null;
  }

  console.log('[OpportunityDetailModal] Rendering modal for:', opportunity.id);

  // Enhance opportunity with computed fields
  const enhancedOpp = enhanceOpportunityEntity(opportunity);
  const opp = enhancedOpp.data;
  const daysUntil = opp.days_until_deadline ?? null;
  const urgency = getDeadlineUrgency(daysUntil);
  const isUrgent = urgency === 'urgent';
  const isExpired = urgency === 'expired';
  const isSoon = urgency === 'soon';
  
  // Get key stats
  const opportunityValue = getOpportunityValue(opp);
  const projectIdDisplay = getProjectIdDisplay(enhancedOpp);
  const stage = opp.proposal_stage || 'Unknown';
  const duration = opp.duration_display || 'Not specified';
  
  // Get current tracking status
  const currentTrackingStatus = opportunity?.data?.tracking_status || '';
  
  // Get button text and styling based on tracking status
  const getTrackingButtonConfig = () => {
    switch (currentTrackingStatus) {
      case 'watching':
        return {
          text: 'Watching',
          className: 'bg-blue-50 dark:bg-blue-900/20 border-blue-600 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30',
        };
      case 'bidding':
        return {
          text: 'Bidding',
          className: 'bg-green-50 dark:bg-green-900/20 border-green-600 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/30',
        };
      case 'won':
        return {
          text: 'Won',
          className: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-600 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/30',
        };
      case 'lost':
        return {
          text: 'Lost',
          className: 'bg-red-50 dark:bg-red-900/20 border-red-600 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30',
        };
      case 'declined':
        return {
          text: 'Declined',
          className: 'bg-gray-50 dark:bg-gray-800/50 border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800',
        };
      default:
        return {
          text: 'Not Tracking',
          className: '',
        };
    }
  };
  
  const trackingButtonConfig = getTrackingButtonConfig();

  const modalContent = (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-[9998]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4 z-[9999]">
        <div className="relative w-full max-w-6xl bg-white dark:bg-theme-paper rounded-xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
          {/* Enhanced Header with Comprehensive Stats - Condensed */}
          <div className="px-6 py-3 border-b border-theme-border bg-gradient-to-r from-gray-50 to-white dark:from-gray-900 dark:to-theme-paper">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-bold text-gray-900 dark:text-theme-text mb-1.5 line-clamp-2">
                  {opp.title}
                </h2>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-theme-text-secondary">
                    <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">{opp.organization || opp.issuing_agency || 'Unknown'}</span>
                  </div>
                  {opp.source && (
                    <Badge variant="default" className="text-xs py-0.5 px-2">
                      <Tag className="h-3 w-3 mr-1" />
                      {opp.source}
                    </Badge>
                  )}
                  {opp.status && (
                    <Badge
                      variant={
                        opp.status === 'open' ? 'success' : opp.status === 'closed' ? 'default' : 'warning'
                      }
                      className="text-xs py-0.5 px-2"
                    >
                      {opp.status}
                    </Badge>
                  )}
                  {opp.match_score !== undefined && opp.match_score !== null && (
                    <Badge variant="default" className="bg-yellow-100 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-xs py-0.5 px-2">
                      <Star className="h-3 w-3 mr-1 text-yellow-600 dark:text-yellow-400 fill-current" />
                      {(opp.match_score * 100).toFixed(0)}% Match
                    </Badge>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="ml-2 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors flex-shrink-0"
              >
                <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
          </div>
          
          {/* Comprehensive Stats Section - Condensed */}
          <div className="px-6 py-3 border-b border-theme-border bg-white dark:bg-theme-paper">
            <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-3">
              {/* Project ID - Span 2 columns to use more space */}
              <div className="flex flex-col col-span-2 min-w-0">
                <span className="text-xs font-medium text-gray-500 dark:text-theme-text-secondary mb-0.5">Project ID</span>
                <span 
                  className="text-xs font-mono font-semibold text-gray-900 dark:text-theme-text break-all" 
                  title={projectIdDisplay}
                >
                  {projectIdDisplay}
                </span>
              </div>
              
              {/* Proposal Stage */}
              {stage !== 'Unknown' && (
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-500 dark:text-theme-text-secondary mb-0.5">Stage</span>
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border w-fit ${getStageBadgeColor(stage)}`}>
                    {stage}
                  </span>
                </div>
              )}
              
              {/* Dollar Value */}
              {opportunityValue !== null && (
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-500 dark:text-theme-text-secondary mb-0.5">Value</span>
                  <div className="flex items-center gap-1">
                    <DollarSign className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                    <span className="text-xs font-semibold text-green-700 dark:text-green-300">
                      {formatCurrency(opportunityValue, { compact: true, symbol: opp.currency === 'CAD' ? 'C$' : '$' })}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Duration */}
              {duration !== 'Not specified' && (
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-500 dark:text-theme-text-secondary mb-0.5">Duration</span>
                  <div className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5 text-gray-600 dark:text-theme-text-secondary" />
                    <span className="text-xs font-medium text-gray-900 dark:text-theme-text">{duration}</span>
                  </div>
                </div>
              )}
              
              {/* Deadline */}
              {opp.deadline && (
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-500 dark:text-theme-text-secondary mb-0.5">Deadline</span>
                  <div className="flex items-center gap-1">
                    <Calendar className={`h-3.5 w-3.5 ${
                      isUrgent ? 'text-red-600 dark:text-red-400' : 
                      isExpired ? 'text-gray-400' : 
                      isSoon ? 'text-yellow-600 dark:text-yellow-400' : 
                      'text-blue-600 dark:text-blue-400'
                    }`} />
                    <div className="flex flex-col">
                      <span className={`text-xs font-semibold ${
                        isUrgent ? 'text-red-700 dark:text-red-300' : 
                        isExpired ? 'text-gray-500 dark:text-gray-400' : 
                        isSoon ? 'text-yellow-700 dark:text-yellow-300' : 
                        'text-blue-700 dark:text-blue-300'
                      }`}>
                        {new Date(opp.deadline).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                      <span className={`text-xs ${
                        isUrgent ? 'text-red-600 dark:text-red-400' : 
                        isExpired ? 'text-gray-500 dark:text-gray-400' : 
                        isSoon ? 'text-yellow-600 dark:text-yellow-400' : 
                        'text-blue-600 dark:text-blue-400'
                      }`}>
                        {isExpired
                          ? `Expired ${Math.abs(daysUntil!)} days ago`
                          : daysUntil === 0
                          ? 'Due today!'
                          : daysUntil === 1
                          ? '1 day left'
                          : `${daysUntil} days left`}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Match Score */}
              {opp.match_score !== undefined && opp.match_score !== null && (
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-500 dark:text-theme-text-secondary mb-1">Match Score</span>
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 text-yellow-600 dark:text-yellow-400 fill-current" />
                    <span className="text-sm font-semibold text-yellow-700 dark:text-yellow-300">
                      {(opp.match_score * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="px-6 py-4 border-b border-theme-border bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center gap-2 flex-wrap">
              {opp.url && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(opp.url, '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Original
                </Button>
              )}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  Tracking Status:
                </label>
                <select
                  value={currentTrackingStatus}
                  onChange={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    const newStatus = e.target.value;
                    if (onUpdateTracking && opportunity) {
                      onUpdateTracking(opportunity.id, newStatus);
                      // Optimistically update the local opportunity state
                      if (onOpportunityUpdate && opportunity) {
                        onOpportunityUpdate({
                          ...opportunity,
                          data: {
                            ...opportunity.data,
                            tracking_status: newStatus === '' ? undefined : (newStatus as any),
                          },
                        });
                      }
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className={`min-w-[140px] px-2 py-1 text-sm border border-theme-border rounded bg-white dark:bg-theme-paper text-theme-text focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                    currentTrackingStatus === 'watching'
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-600 text-blue-700 dark:text-blue-300'
                      : currentTrackingStatus === 'bidding'
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-600 text-green-700 dark:text-green-300'
                      : currentTrackingStatus === 'won'
                      ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-600 text-emerald-700 dark:text-emerald-300'
                      : currentTrackingStatus === 'lost'
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-600 text-red-700 dark:text-red-300'
                      : currentTrackingStatus === 'declined'
                      ? 'bg-gray-50 dark:bg-gray-800/50 border-gray-600 text-gray-700 dark:text-gray-300'
                      : ''
                  }`}
                >
                  <option value="">Not Tracking</option>
                  <option value="watching">Watching</option>
                  <option value="bidding">Bidding</option>
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                  <option value="declined">Declined</option>
                </select>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={onGenerateProposal}
              >
                <FileText className="h-4 w-4 mr-2" />
                Generate Proposal
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export PDF
              </Button>
              <Button variant="outline" size="sm">
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex-1 overflow-y-auto p-6">
            <Tabs
              tabs={[
                {
                  id: 'overview',
                  label: 'Overview',
                  content: (
                    <div className="space-y-6">
                {/* Proposal AI Summary - Above Description */}
                <ProposalSummaryCard opportunityId={opportunity.id} />
                
                {opp.description && (
                  <Card className="p-4">
                    <h3 className="font-semibold mb-2 text-gray-900 dark:text-theme-text">Description</h3>
                    <p className="text-gray-700 dark:text-theme-text-secondary whitespace-pre-wrap">
                      {opp.description}
                    </p>
                  </Card>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {opp.value && (
                    <Card className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
                        <h3 className="font-semibold text-gray-900 dark:text-theme-text">Value</h3>
                      </div>
                      <p className="text-2xl font-bold text-green-700 dark:text-green-400">
                        {formatCurrency(opp.value, { compact: true })}
                      </p>
                    </Card>
                  )}

                  {opp.deadline && (
                    <Card className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock
                          className={`h-5 w-5 ${
                            isUrgent ? 'text-red-600' : isExpired ? 'text-gray-400' : 'text-blue-600'
                          }`}
                        />
                        <h3 className="font-semibold text-gray-900 dark:text-theme-text">Deadline</h3>
                      </div>
                      <p className="text-lg font-semibold">
                        {opp.deadline ? new Date(opp.deadline).toLocaleDateString() : 'N/A'}
                      </p>
                      {daysUntil !== null && (
                        <p
                          className={`text-sm mt-1 ${
                            isUrgent
                              ? 'text-red-600 font-semibold'
                              : isExpired
                              ? 'text-gray-500'
                              : 'text-blue-600'
                          }`}
                        >
                          {isExpired
                            ? `${Math.abs(daysUntil)} days overdue`
                            : daysUntil === 0
                            ? 'Due today!'
                            : `${daysUntil} days left`}
                        </p>
                      )}
                    </Card>
                  )}

                  {opp.location && (
                    <Card className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <h3 className="font-semibold text-gray-900 dark:text-theme-text">Location</h3>
                      </div>
                      <p className="text-gray-700 dark:text-theme-text-secondary">{opp.location}</p>
                    </Card>
                  )}

                  {opp.posted_date && (
                    <Card className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        <h3 className="font-semibold text-gray-900 dark:text-theme-text">Posted Date</h3>
                      </div>
                      <p className="text-gray-700 dark:text-theme-text-secondary">
                        {opp.posted_date ? new Date(opp.posted_date).toLocaleDateString() : 'N/A'}
                      </p>
                    </Card>
                  )}
                </div>

                {opp.matching_service_lines && opp.matching_service_lines.length > 0 && (
                  <Card className="p-4">
                    <h3 className="font-semibold mb-3 text-gray-900 dark:text-theme-text">Service Lines</h3>
                    <div className="flex flex-wrap gap-2">
                      {opp.matching_service_lines.map((line) => (
                        <Badge key={line} variant="default" className="bg-blue-100 dark:bg-blue-900/20">
                          {line}
                        </Badge>
                      ))}
                    </div>
                  </Card>
                )}

                {opp.contact_info && (
                  <Card className="p-4">
                    <h3 className="font-semibold mb-2 text-gray-900 dark:text-theme-text">Contact Information</h3>
                    <p className="text-gray-700 dark:text-theme-text-secondary whitespace-pre-wrap">
                      {opp.contact_info}
                    </p>
                  </Card>
                )}
                    </div>
                  ),
                },
                {
                  id: 'requirements',
                  label: 'Requirements',
                  content: (
                    <RequirementsTab opportunityId={opportunity.id} />
                  ),
                },
                {
                  id: 'analysis',
                  label: 'Analysis',
                  content: (
                    <div className="space-y-4">
                {analysisLoading ? (
                  <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200 dark:from-purple-900/20 dark:to-indigo-900/20 dark:border-purple-700">
                    {/* Enhanced Streaming status indicator with rotating messages */}
                    <div className="mb-4 p-4 bg-gradient-to-r from-purple-100 via-indigo-100 to-purple-100 dark:from-purple-800/30 dark:via-indigo-800/30 dark:to-purple-800/30 rounded-lg border-2 border-purple-300 dark:border-purple-600 shadow-lg">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="relative">
                          {analysisStage === 'retrieval' || analysisStage === 'idle' ? (
                            <Search className="h-5 w-5 text-purple-600 dark:text-purple-400 animate-bounce" />
                          ) : analysisStage === 'thinking' ? (
                            <Brain className="h-5 w-5 text-purple-600 dark:text-purple-400 animate-pulse" />
                          ) : (
                            <Zap className="h-5 w-5 text-purple-600 dark:text-purple-400 animate-spin" />
                          )}
                          <span className="absolute -top-1 -right-1 flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 dark:bg-purple-500 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-500 dark:bg-purple-400"></span>
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-purple-900 dark:text-purple-100 mb-1">
                            {analysisStage === 'retrieval' ? 'Searching knowledge base...' :
                             analysisStage === 'thinking' ? 'Analyzing capabilities...' :
                             analysisStage === 'generating' ? 'Generating insights...' :
                             'Starting analysis...'}
                          </div>
                          <div className="text-xs text-purple-700 dark:text-purple-300 animate-pulse">
                            {loadingMessages[loadingMessageIndex]}
                          </div>
                        </div>
                        {analysisElapsed > 0 && (
                          <div className="text-right">
                            <div className="text-lg font-bold text-purple-600 dark:text-purple-400">{analysisElapsed.toFixed(1)}s</div>
                            <div className="text-xs text-purple-500 dark:text-purple-400">elapsed</div>
                          </div>
                        )}
                      </div>
                      
                      {/* Enhanced Progress bar with stages */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs text-purple-700 dark:text-purple-300 font-medium">
                          <span className={analysisStage === 'retrieval' || analysisStage === 'idle' ? 'text-purple-900 dark:text-purple-100' : ''}>
                            🔍 Search
                          </span>
                          <span className={analysisStage === 'thinking' ? 'text-purple-900 dark:text-purple-100' : ''}>
                            🧠 Think
                          </span>
                          <span className={analysisStage === 'generating' ? 'text-purple-900 dark:text-purple-100' : ''}>
                            ✍️ Write
                          </span>
                          <span className={analysisStage === 'complete' ? 'text-purple-900 dark:text-purple-100' : ''}>
                            ✅ Done
                          </span>
                        </div>
                        <div className="h-2 bg-purple-200 dark:bg-purple-800 rounded-full overflow-hidden shadow-inner">
                          <div
                            className="h-full bg-gradient-to-r from-purple-500 via-indigo-500 to-purple-600 dark:from-purple-400 dark:via-indigo-400 dark:to-purple-500 transition-all duration-500 ease-out relative overflow-hidden"
                            style={{
                              width: analysisStage === 'retrieval' || analysisStage === 'idle' ? '25%' :
                                     analysisStage === 'thinking' ? '50%' :
                                     analysisStage === 'generating' ? '75%' :
                                     analysisStage === 'complete' ? '100%' : '10%',
                            }}
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-pulse"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                ) : analysisError ? (
                  <Card className="p-8 text-center">
                    <AlertTriangle className="h-12 w-12 text-red-400 dark:text-red-600 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-theme-text-secondary mb-2">
                      Failed to load analysis
                    </p>
                    <p className="text-sm text-gray-500 dark:text-theme-text-muted">
                      {analysisErrorDetail instanceof Error ? analysisErrorDetail.message : 'An unexpected error occurred'}
                    </p>
                  </Card>
                ) : analysis ? (
                  <>
                    <Card className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-gray-900 dark:text-theme-text">Capability Match Score</h3>
                        <div className="text-3xl font-bold text-primary-600 dark:text-primary-400">
                          {(analysis.capability_match_score * 100).toFixed(0)}%
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4">
                        <div
                          className="bg-primary-600 h-4 rounded-full transition-all"
                          style={{ width: `${analysis.capability_match_score * 100}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-theme-text-muted mt-2">
                        Source: {analysis.analysis_source === 'rag_analysis' ? 'AI Analysis' : 'Basic Analysis'}
                      </p>
                    </Card>

                    {analysis.summary && (
                      <Card className="p-4">
                        <h3 className="font-semibold mb-3 text-gray-900 dark:text-theme-text">Analysis Summary</h3>
                        <div 
                          className="text-gray-700 dark:text-theme-text-secondary leading-relaxed prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: convertMarkdownToHTML(analysis.summary) }}
                        />
                      </Card>
                    )}

                    {analysis.technical_approaches && analysis.technical_approaches.length > 0 && (
                      <Card className="p-4">
                        <h3 className="font-semibold mb-3 text-gray-900 dark:text-theme-text">Technical Approaches</h3>
                        <div className="space-y-3">
                          {analysis.technical_approaches.map((approach: any, idx: number) => (
                            <div
                              key={idx}
                              className="p-3 border border-theme-border rounded-lg bg-blue-50 dark:bg-blue-900/20"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-gray-900 dark:text-theme-text">
                                  {approach.title || 'Technical Approach'}
                                </span>
                                <Badge variant="default">
                                  {Math.min(100, Math.max(0, (approach.relevance_score || 0) * 100)).toFixed(1)}% relevant
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-700 dark:text-theme-text-secondary">
                                {approach.description}
                              </p>
                              {approach.source && (
                                <p className="text-xs text-gray-500 dark:text-theme-text-muted mt-1">
                                  Source: {approach.source}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}

                    {analysis.similar_projects && analysis.similar_projects.length > 0 && (
                      <Card className="p-4">
                        <h3 className="font-semibold mb-3 text-gray-900 dark:text-theme-text">Similar Past Projects</h3>
                        <div className="space-y-3">
                          {analysis.similar_projects.map((project: any, idx: number) => (
                            <div
                              key={idx}
                              className="p-3 border border-theme-border rounded-lg bg-gray-50 dark:bg-gray-800/50"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-gray-900 dark:text-theme-text">
                                  {project.project_name}
                                </span>
                                <Badge variant="default">
                                  {Math.min(100, Math.max(0, (project.relevance_score || 0) * 100)).toFixed(1)}% match
                                </Badge>
                              </div>
                              {project.similarity_reasons && project.similarity_reasons.length > 0 && (
                                <ul className="text-sm text-gray-600 dark:text-theme-text-secondary list-disc list-inside">
                                  {project.similarity_reasons.map((reason: string, i: number) => (
                                    <li key={i}>{reason}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}

                    {analysis.staff_allocation && analysis.staff_allocation.length > 0 && (
                      <Card className="p-4">
                        <h3 className="font-semibold mb-3 text-gray-900 dark:text-theme-text">Recommended Staff Allocation</h3>
                        <div className="space-y-3">
                          {analysis.staff_allocation.map((staff: any, idx: number) => (
                            <div
                              key={idx}
                              className="p-3 border border-theme-border rounded-lg bg-purple-50 dark:bg-purple-900/20"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <span className="font-medium text-gray-900 dark:text-theme-text">
                                    {staff.name}
                                  </span>
                                  {staff.role && (
                                    <span className="text-sm text-gray-600 dark:text-theme-text-secondary ml-2">
                                      - {staff.role}
                                    </span>
                                  )}
                                </div>
                                <Badge variant="default">
                                  {Math.min(100, Math.max(0, (staff.match_score || 0) * 100)).toFixed(1)}% match
                                </Badge>
                              </div>
                              {staff.qualifications && staff.qualifications.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {staff.qualifications.map((qual: string, i: number) => (
                                    <Badge key={i} variant="default" className="text-xs">
                                      {qual}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}

                    {analysis.recommendations && analysis.recommendations.length > 0 && (
                      <Card className="p-4">
                        <h3 className="font-semibold mb-3 text-gray-900 dark:text-theme-text">Strategic Recommendations</h3>
                        <div 
                          className="text-gray-700 dark:text-theme-text-secondary leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: convertMarkdownToHTML(analysis.recommendations.join('\n\n')) }}
                        />
                      </Card>
                    )}

                    {/* Enhanced Citations Section with Artifact Details */}
                    {analysis.citations && analysis.citations.length > 0 && (
                      <Card className="p-4">
                        <h3 className="text-base font-bold text-gray-900 dark:text-theme-text mb-4 flex items-center gap-2">
                          <FileText className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                          Evidence Sources ({analysis.citations.length})
                        </h3>
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {analysis.citations.map((citation: any, idx: number) => {
                            const isExpanded = expandedCitation === idx;
                            
                            return (
                              <div
                                key={citation.id || idx}
                                className="bg-gradient-to-r from-white to-primary-50 dark:from-gray-800 dark:to-primary-900/20 rounded-lg border-2 border-primary-200 dark:border-primary-700 hover:border-primary-400 dark:hover:border-primary-600 transition-all shadow-sm hover:shadow-md"
                              >
                                {/* Citation Header */}
                                <div className="p-4">
                                  <div className="flex items-start gap-3">
                                    <div className="flex-shrink-0">
                                      <div className="w-8 h-8 rounded-full bg-primary-600 dark:bg-primary-500 text-white flex items-center justify-center font-bold text-sm">
                                        {citation.id || citation.rank || idx + 1}
                                      </div>
                                    </div>
                                    
                                    <div className="flex-1 min-w-0">
                                      {/* Source Document Info */}
                                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                                        <FileText className="h-4 w-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                                        <span className="text-sm font-bold text-theme-text truncate" title={citation.artifact_name || citation.artifact_filename || citation.source || citation.artifact_id || 'Unknown Document'}>
                                          {(() => {
                                            // Try multiple fields to get the document name (artifact_filename is from RAG response)
                                            const name = citation.artifact_name || 
                                                         (citation as any).artifact_filename ||
                                                         citation.source || 
                                                         (citation.artifact_id ? `Document ${citation.artifact_id.substring(0, 8)}` : null) ||
                                                         (citation.evidence_id ? `Evidence ${citation.evidence_id.substring(0, 8)}` : null) ||
                                                         (citation.chunk_id ? `Chunk ${citation.chunk_id.substring(0, 8)}` : null) ||
                                                         'Unknown Document';
                                            return name;
                                          })()}
                                        </span>
                                        {citation.confidence && (
                                          <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200 text-xs">
                                            {Math.min(100, Math.max(0, (citation.confidence * 100))).toFixed(1)}% match
                                          </Badge>
                                        )}
                                        {citation.relevance_score && (
                                          <Badge variant="default" className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 text-xs">
                                            {Math.min(100, Math.max(0, (citation.relevance_score * 100))).toFixed(1)}% relevant
                                          </Badge>
                                        )}
                                      </div>
                                      
                                      {/* Metadata Tags */}
                                      <div className="flex items-center gap-2 mb-2 flex-wrap text-xs">
                                        {citation.page && (
                                          <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 rounded-md font-medium">
                                            📄 Page {citation.page}
                                          </span>
                                        )}
                                        {citation.section && (
                                          <span className="px-2 py-1 bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-200 rounded-md font-medium">
                                            📑 {citation.section}
                                          </span>
                                        )}
                                        {citation.section_path && (
                                          <span className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200 rounded-md font-medium">
                                            📑 {citation.section_path}
                                          </span>
                                        )}
                                        {citation.chunk_id && (
                                          <span className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200 rounded-md font-mono text-xs">
                                            ID: {citation.chunk_id.substring(0, 8)}...
                                          </span>
                                        )}
                                        {citation.rank && (
                                          <span className="px-2 py-1 bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 rounded-md font-medium">
                                            Rank: {citation.rank}
                                          </span>
                                        )}
                                      </div>
                                      
                                      {/* Preview Content */}
                                      {citation.content && (
                                        <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded-md border border-primary-100 dark:border-primary-800">
                                          <div className="text-xs font-semibold text-primary-700 dark:text-primary-300 mb-2 flex items-center gap-1">
                                            <span>📝</span>
                                            <span>Relevant Excerpt:</span>
                                          </div>
                                          <div className={`text-sm text-theme-text leading-relaxed ${!isExpanded ? 'line-clamp-3' : ''}`}>
                                            "{citation.content}"
                                          </div>
                                          {citation.content.length > 200 && (
                                            <button
                                              onClick={() => setExpandedCitation(isExpanded ? null : idx)}
                                              className="mt-2 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium flex items-center gap-1"
                                            >
                                              {isExpanded ? (
                                                <>
                                                  <ChevronDown className="h-3 w-3" />
                                                  Show less
                                                </>
                                              ) : (
                                                <>
                                                  <ChevronRight className="h-3 w-3" />
                                                  Read more
                                                </>
                                              )}
                                            </button>
                                          )}
                                        </div>
                                      )}
                                      
                                      {/* Action Buttons */}
                                      <div className="mt-3 flex items-center gap-2">
                                        {citation.artifact_id && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                              // Navigate to Knowledge Base with this artifact
                                              window.location.href = `/knowledge?artifact=${citation.artifact_id}`;
                                            }}
                                            className="text-xs"
                                          >
                                            <FileText className="h-3 w-3 mr-1" />
                                            View Document
                                          </Button>
                                        )}
                                        {citation.chunk_id && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                              // Navigate to Evidence Viewer with this chunk
                                              window.location.href = `/evidence?chunk=${citation.chunk_id}`;
                                            }}
                                            className="text-xs"
                                          >
                                            <Search className="h-3 w-3 mr-1" />
                                            View Chunk
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Help Text */}
                        <div className="mt-4 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-700">
                          <p className="text-xs text-primary-700 dark:text-primary-300">
                            💡 <span className="font-semibold">Tip:</span> Click "View Document" to see the full source file, or "View Chunk" to explore the specific evidence section.
                          </p>
                        </div>
                      </Card>
                    )}
                  </>
                ) : (
                  <Card className="p-8 text-center">
                    <AlertTriangle className="h-12 w-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-theme-text-secondary">
                      Analysis not available. Please try again later.
                    </p>
                  </Card>
                )}
                    </div>
                  ),
                },
                {
                  id: 'documents',
                  label: 'Documents',
                  content: (
                    <OpportunityDocumentsTab
                      opportunityId={opportunity.id}
                      projectId={projectId || ''}
                    />
                  ),
                },
                {
                  id: 'email-draft',
                  label: 'Email Draft',
                  content: (
                    <OpportunityEmailDraftTab
                      opportunityId={opportunity.id}
                      projectId={projectId || ''}
                      opportunity={enhancedOpp}
                    />
                  ),
                },
              ]}
              defaultTab={activeTab}
              onChange={(tabId) => {
                setActiveTab(tabId as any);
                // Trigger analysis fetch when Analysis tab is opened
                if (tabId === 'analysis' && opportunity?.id) {
                  // Query will auto-fetch due to enabled condition
                }
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Render modal using portal to body to avoid z-index issues
  if (typeof document === 'undefined') {
    console.log('[OpportunityDetailModal] document is undefined, cannot render portal');
    return null;
  }

  console.log('[OpportunityDetailModal] Rendering portal to document.body');
  return createPortal(modalContent, document.body);
}

